import { InjectionToken } from '@angular/core';
import { ConditionOperator } from '@app/core/enums/condition-operator.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { OrganizationType } from '@app/core/enums/organization-type.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { AbstractBean } from '@app/core/model/abstract-bean';
import { FieldPermission } from '@app/core/model/client/group';
import { AssetType } from '@app/core/model/entities/asset/asset-type';
import { Entity, RelatedEntity } from '@app/core/model/entities/entity';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { StylesConditions } from '@app/shared/extra/utils';
import { Expose, Type } from 'class-transformer';
import { Observable } from 'rxjs';

export const FIELD_GROUP_CONFIG_INJECTION = new InjectionToken<FieldGroup>('field_group_config_injection');
export const FIELD_CONFIG_INJECTION = new InjectionToken<FieldConfig>('field_config_injection');
export const FIELD_ENTITY_INJECTION = new InjectionToken<Entity>('field_entity_injection');
export const FIELD_PRECONDITIONS_INJECTION = new InjectionToken<boolean>('field_preconditions_injection');
export const FIELD_ASYNC_PRECONDITIONS_INJECTION = new InjectionToken<Observable<boolean>>(
  'field_async_preconditions_injection'
);
export const FIELD_PERMISSIONS_INJECTION = new InjectionToken<string[]>('field_permissions_injection');
export const FIELD_EXTRA_DATA = new InjectionToken<any>('field_data_injection');
export const FIELD_EVENTS_ORIGIN = new InjectionToken<any>('field_events_origin');
export const SECTION_ENTITY_TYPE_INJECTION = new InjectionToken<any>('section_entity_type_injection');
export const SECTION_CREATES_ENTITY_INJECTION = new InjectionToken<any>('section_creates_entity_injection');

export interface FormNode {
  code: string;
  label?: string;
  order?: number;
  hierarchy?: string[];
  graphQlTypeName: string;

  children: FormNode[];
}

export class Form implements FormNode {
  public code: string;
  public label?: string;
  public entityType: EntityTypeEnum;

  public children: Section[];

  public graphQlTypeName = 'Form';
}

/**
 * SectionInput for creating Sections.
 */
export interface CreateSectionInput {
  code: string;
  label: string;
  order: number;
  assetTypeIds?: string[];
  allAssetTypes?: boolean;
  entityType: EntityTypeEnum;
  formCode: string;
}

/**
 * SectionInput for updating Sections.
 */
export interface UpdateSectionInput {
  label: string;
  order: number;
  assetTypeIds?: string[];
  allAssetTypes?: boolean;
  customOptions?: Record<string, any>;
  fieldGroups?: FieldGroupInput[];
  conditionsToView?: Condition[];
  conditionsToEdit?: Condition[];
}

export interface FieldGroupInput {
  code: string;
  label: string;
  order: number;
  tooltip?: string;
  emptyLabel?: string;
  fieldGroupType?: string;
  customOptions?: Record<string, any>;
  fieldConfigs?: FieldConfigInput[];
  conditionsToView?: Condition[];
  conditionsToEdit?: Condition[];
}

export interface FieldConfigInput {
  fieldCode: string;
  order: number;
  customOptions?: Record<string, any>;
  conditionsToView?: Condition[];
  conditionsToEdit?: Condition[];
}

export class Section extends AbstractBean implements FormNode {
  public code: string;
  public order: number;
  public formCode: string;
  public organizationId: string;
  public entityType: EntityTypeEnum;

  public label?: string;
  public emptyLabel?: string;

  public customOptions?: {
    isCreatingEntity?: boolean,
    required?: boolean,
    [option: string]: any
  };

  @Expose({name: '__typename'})
  public graphQlTypeName: string;

  @Expose({name: 'fieldGroupsList'})
  @Type(() => FieldGroup)
  public children: FieldGroup[];
  @Expose({name: 'conditionsToViewList'})
  public conditionsToView: Condition[];
  @Expose({name: 'conditionsToEditList'})
  public conditionsToEdit: Condition[];

  public allAssetTypes?: boolean;
  @Expose({name: 'assetTypesList'})
  public assetTypes: AssetType[];
}

export class FieldGroup extends AbstractBean implements FormNode {
  public code: string;
  public order: number;
  public fieldGroupType: string;

  public label: string;
  public emptyLabel?: string;
  public tooltip?: string;

  public customOptions?: {
    propertiesPath?: string,
    disabled?: boolean,
    [option: string]: any
  };

  @Expose({name: '__typename'})
  public graphQlTypeName: string;

  @Expose({name: 'fieldConfigsList'})
  @Type(() => FieldConfig)
  public children: FieldConfig[];
  @Expose({name: 'conditionsToViewList'})
  public conditionsToView: Condition[];
  @Expose({name: 'conditionsToEditList'})
  public conditionsToEdit: Condition[];

  /**
   * Path to the properties referenced by this field group within the current entity.
   */
  public get propertiesPath(): string {
    return this.customOptions?.propertiesPath;
  }

  /**
   * Classification code that must be used to load classification categories for a field group of type 'classification'.
   */
  public get classificationCode(): string {
    return this.customOptions?.['classificationCode'];
  }
}

export class FieldConfig extends AbstractBean implements FormNode {
  public fieldCode: string;
  public order: number;

  public customOptions?: {
    suffixType?: string,
    formatType?: FormatType,
    pictograms?: { value: string, image: string }[],
    withText?: boolean,
    customCss?: StylesConditions,
    colored?: boolean,
    translate?: boolean,
    relatedEntity?: boolean,
    required?: boolean,
    // Column properties
    chartDataType?: string,
    displayMobile?: string,
    aggFuncs?: string[],
    defaultAggFunc?: string,
    pinned?: boolean,
    noFilter?: boolean,
    propertiesPath?: string,
    defaultColumnState?: Record<string, any>,
    [option: string]: any
  };

  @Expose({name: '__typename'})
  public graphQlTypeName: string;

  @Type(() => Field)
  public field: Field;
  @Expose({name: 'conditionsToViewList'})
  public conditionsToView: Condition[];
  @Expose({name: 'conditionsToEditList'})
  public conditionsToEdit: Condition[];

  /**
   * A FieldConfig is a leaf node in the tree, so it has no children.
   */
  public get children(): FormNode[] {
    return [];
  }

  /**
   * Retrieves the label for a pictogram based on the provided key.
   * @param {string} key The key used to identify the specific pictogram.
   * @returns {string} The pictogram associated with the given key. Returns undefined if the key is not found.
   */
  public pictogram(key: string): string {
    return this.customOptions?.pictograms?.find(p => p.value === key)?.image;
  }

  /**
   * Field code associated with this FieldConfig.
   */
  public get code(): string {
    return this.fieldCode;
  }

  /**
   * List of pictograms to use if exists.
   * @returns {object} Returns an object that contains pairs of key/value.
   * The value is the name of the pictogram to render.
   */
  public get pictograms(): Record<string, any> {
    return this.customOptions?.pictograms;
  }

  /**
   * Boolean that returns true if the field contains pictograms.
   * @returns {boolean} Returns true if the field contains pictograms, false otherwise.
   */
  public get hasPictograms(): boolean {
    return !!this.customOptions?.pictograms?.length;
  }

  /**
   * Boolean that returns true if the field should be displayed with a pictogram and a text.
   * @returns {boolean} Returns true if the field's pictogram should be displayed with a text, false otherwise.
   */
  public get isPictogramWithText(): boolean {
    return this.customOptions?.withText ?? false;
  }

  /**
   * Field's entityType.
   */
  public get fieldEntityType(): EntityTypeEnum {
    return this.field.entityType;
  }

  /**
   * Field's label.
   */
  public get label(): string {
    return this.field.label;
  }

  /**
   * Prefixed translation key of the field group's tooltip text, or undefined if the field group has no tooltip.
   */
  public get tooltip(): string {
    return this.field.tooltip;
  }

  /**
   * Prefixed translation key of the field group's suffix (i.e. unit), or undefined if the field group has no suffix.
   */
  public get suffixType(): string {
    return this.customOptions?.suffixType && 'SUFFIX.' + this.customOptions?.suffixType.toUpperCase();
  }

  /**
   * Return the field's path as an Array of string
   */
  public get fieldPath(): string[] {
    return this.field.parentPath.concat(this.fieldCode);
  }

  /**
   * Whether the field is read-only (not editable)
   */
  public get computed(): boolean {
    return !!this.field.computed;
  }

  /**
   * CSS properties that should be applied to Field/Section/Cell in conditional form
   * @return Object containing css properties as keys and a list of condition/value as value
   */
  public get customCss(): StylesConditions {
    return this.customOptions?.customCss ?? {};
  }

  /**
   * Returns the classification level of the field.
   * @returns A numeric representation of the classification level of the field.
   */
  public get classificationLevel(): number {
    return this.customOptions?.['classificationLevel'];
  }
}

/**
 * FieldInputs for creating and updating Fields.
 */
export type UpdateFieldInput = {
  label: string;
  tooltip: string;
};

export interface CreateFieldInput {
  code: string;
  label: string;
  tooltip: string;
  entityType: EntityTypeEnum;
  fieldType: FieldTypeEnum;
  validators?: FieldValidator[];
  fieldValues?: string[];
}

export interface CreateReferenceIndicatorInput {
  refFieldCode: string;
  refEntityType: EntityTypeEnum;
  entityType: EntityTypeEnum;
  code: string;
  label: string;
  tooltip?: string;
}

export class Field extends AbstractBean {
  public code: string;
  public fieldType: FieldTypeEnum;
  public entityType: EntityTypeEnum;
  public checkType: string;
  public organizationId: string;
  public computed: boolean;
  @Expose({name: 'fieldValuesList'})
  public fieldValues?: any[];
  @Expose({name: 'validatorsList'})
  @Type(() => FieldValidator)
  public validators?: FieldValidator[];
  @Expose({name: 'parentPathList'})
  public parentPath: string[];
  @Expose({name: 'formulaList'})
  public formula?: Record<string, any>[];
  public classificationCode: string;

  public label: string;
  public tooltip?: string;

  /**
   * Return the field's path as an Array of string
   */
  public get fieldPath(): string[] {
    return this.parentPath.concat(this.code);
  }

  /**
   * Whether a field is root.
   */
  public get isRoot(): boolean {
    return this.parentPath.length === 0;
  }

  /**
   * Get the list of field's permissions this field can have.
   * - Root fields can't have NONE.
   * - Computed fields can't have WRITE or WRITE_ON_CREATE.
   * @return List of available permissions
   */
  public get availableFieldPermissions(): FieldPermission[] {
    return Object.values(FieldPermission)
      // Root or related entities fields must have a permission set
      .filter(permission => permission != FieldPermission.NONE || !(this.isRoot || this.isRelatedEntity))
      // Computed fields can't have write permissions since they are not editable.
      .filter(permission => ![FieldPermission.WRITE, FieldPermission.WRITE_ON_CREATE].includes(permission) || !this.computed);
  }

  /**
   * Whether the field's type is part of the related entity fields.
   * @return True if the entity field type is related, false otherwise.
   */
  public get isRelatedEntity(): boolean {
    return RelatedEntity.relatedEntityFields.includes(this.fieldType);
  }

  /**
   * Whether the field's type is array like.
   * @return True if the field's type is an array, false otherwise.
   */
  public get isFieldTypeArrayLike(): boolean {
    return [
      FieldTypeEnum.CHIPS,
      FieldTypeEnum.CHIPS_AUTOCOMPLETE,
      FieldTypeEnum.LIST_MULTIVALUE,
      FieldTypeEnum.PROJECTS,
      FieldTypeEnum.EQUIPMENTS,
      FieldTypeEnum.SPACES,
      FieldTypeEnum.DOCUMENT_RELATED_ASSETS,
      FieldTypeEnum.ASSETS
    ]
      .includes(this.fieldType);
  }

}

export interface Condition {
  field: string;
  operator: ConditionOperator;
  value?: any;
}

export interface DefaultField {
  code: string;
  locale: string;
  organizationType: OrganizationType;
  isTb: boolean;
  label?: string;
  entityType?: EntityTypeEnum;
  fieldType?: FieldTypeEnum;
  computed?: boolean;
}

export interface DefaultFieldInput {
  code: string;
  locale: string;
  organizationType: OrganizationType;
  isTb: boolean;
  label?: string;
  entityType?: EntityTypeEnum;
  validators?: FieldValidator[];
}

export declare type FormatType =
  'integer'
  | 'percent'
  | 'precise_number'
  | 'numeric'
  | 'duration'
  | 'scientific'
  | 'engineering'
  | 'currency'
  | 'decimal_currency'
  | 'precise_currency'
  | 'engineering_currency';

export declare type SuffixType =
  'per_year'
  | 'excluding_taxes'
  | 'including_taxes'
  | 'per_squarefoot'
  | 'per_squaremeter'
  | 'per_squaremeter_year'
  | 'floor'
  | 'kgeq'
  | 'kgeqco2_kwh_year'
  | 'kgeqco2_squaremeter'
  | 'kgeqco2_squaremeter_year'
  | 'kmh'
  | 'kw'
  | 'kwh'
  | 'kwhpe'
  | 'kwhpe_squaremeter'
  | 'kwhpe_year'
  | 'kwh_squaremeter'
  | 'kwh_squaremeter_year'
  | 'kwh_year'
  | 'year'
  | 'month'
  | 'hour'
  | 'percent'
  | 'squarefoot'
  | 'squaremeter'
  | 'unit'
  | 'mm'
  | 'm'
  | 'cm'
  | 'repkman';

/**
 * Check if a validatorType is field dependant or not
 * @param type : validator type
 *
 * @return true if validatorType is fieldDependant otherwise false
 */
export function isValidatorFieldDependant(type: ValidatorType): boolean {
  return [
    ValidatorType.AFTER_OTHER_DATE,
    ValidatorType.BEFORE_OTHER_DATE
  ]
    .includes(type);
}

export type FieldFilterOptions = {
  computed?: 'ONLY_COMPUTED' | 'NOT_COMPUTED' | 'ALL';
  entityType?: EntityTypeEnum;
  assetTypes?: string[];
  noAssetTypes?: boolean;
  excludeConfigImmutable?: boolean;
};
